import React, { useState } from 'react';
import { BarChart2Icon, PieChartIcon, TrendingUpIcon, CalendarIcon, DownloadIcon, FilterIcon, RefreshCwIcon, UserIcon, TagIcon, DollarSignIcon } from 'lucide-react';
export function ExpenseReports({
  locale = 'en-US'
}) {
  const [dateRange, setDateRange] = useState('last3months');
  const [groupBy, setGroupBy] = useState('category');
  const [reportType, setReportType] = useState('summary');
  // Format currency based on locale
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  // Sample data for reports
  const reportData = {
    totalExpenses: 45750.25,
    expensesByCategory: [{
      category: 'Travel',
      amount: 18250.75,
      percentage: 39.9
    }, {
      category: 'Training',
      amount: 8750.5,
      percentage: 19.1
    }, {
      category: 'Meals',
      amount: 5980.25,
      percentage: 13.1
    }, {
      category: 'Marketing',
      amount: 4520.5,
      percentage: 9.9
    }, {
      category: 'Software',
      amount: 3950.0,
      percentage: 8.6
    }, {
      category: 'Office',
      amount: 2850.75,
      percentage: 6.2
    }, {
      category: 'Other',
      amount: 1447.5,
      percentage: 3.2
    }],
    expensesByDepartment: [{
      department: 'Sales',
      amount: 15250.5,
      percentage: 33.3
    }, {
      department: 'Marketing',
      amount: 12750.75,
      percentage: 27.9
    }, {
      department: 'Engineering',
      amount: 8950.0,
      percentage: 19.6
    }, {
      department: 'Finance',
      amount: 4520.5,
      percentage: 9.9
    }, {
      department: 'HR',
      amount: 2450.25,
      percentage: 5.4
    }, {
      department: 'Operations',
      amount: 1828.25,
      percentage: 4.0
    }],
    monthlyTrend: [{
      month: 'Jun',
      amount: 8250.5
    }, {
      month: 'Jul',
      amount: 9750.25
    }, {
      month: 'Aug',
      amount: 12500.75
    }, {
      month: 'Sep',
      amount: 7850.5
    }, {
      month: 'Oct',
      amount: 7398.25
    }],
    topExpenseUsers: [{
      name: 'John Doe',
      amount: 7850.75,
      count: 12
    }, {
      name: 'Sarah Williams',
      amount: 6950.5,
      count: 8
    }, {
      name: 'Robert Brown',
      amount: 5750.25,
      count: 10
    }, {
      name: 'Jane Smith',
      amount: 4850.5,
      count: 7
    }, {
      name: 'Michael Johnson',
      amount: 3750.25,
      count: 6
    }]
  };
  const getDataByGrouping = () => {
    switch (groupBy) {
      case 'department':
        return reportData.expensesByDepartment;
      case 'user':
        return reportData.topExpenseUsers.map(user => ({
          label: user.name,
          amount: user.amount,
          percentage: user.amount / reportData.totalExpenses * 100
        }));
      case 'category':
      default:
        return reportData.expensesByCategory;
    }
  };
  const getGroupLabel = () => {
    switch (groupBy) {
      case 'department':
        return 'Department';
      case 'user':
        return 'User';
      case 'category':
      default:
        return 'Category';
    }
  };
  return <div data-id="element-7713">
      <div className="bg-white shadow overflow-hidden sm:rounded-md mb-6" data-id="element-7714">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-7715">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7716">
            Expense Reports
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-7717">
            Generate and view expense reports by different criteria
          </p>
        </div>
        {/* Report controls */}
        <div className="p-4 border-b border-gray-200 sm:p-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-7718">
          <div data-id="element-7719">
            <label htmlFor="date-range" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7720">
              Date Range
            </label>
            <select id="date-range" value={dateRange} onChange={e => setDateRange(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-7721">
              <option value="last3months" data-id="element-7722">Last 3 Months</option>
              <option value="last6months" data-id="element-7723">Last 6 Months</option>
              <option value="lastyear" data-id="element-7724">Last Year</option>
              <option value="ytd" data-id="element-7725">Year to Date</option>
              <option value="custom" data-id="element-7726">Custom Range</option>
            </select>
          </div>
          <div data-id="element-7727">
            <label htmlFor="group-by" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7728">
              Group By
            </label>
            <select id="group-by" value={groupBy} onChange={e => setGroupBy(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-7729">
              <option value="category" data-id="element-7730">Category</option>
              <option value="department" data-id="element-7731">Department</option>
              <option value="user" data-id="element-7732">User</option>
            </select>
          </div>
          <div data-id="element-7733">
            <label htmlFor="report-type" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7734">
              Report Type
            </label>
            <select id="report-type" value={reportType} onChange={e => setReportType(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-7735">
              <option value="summary" data-id="element-7736">Summary</option>
              <option value="detailed" data-id="element-7737">Detailed</option>
              <option value="trend" data-id="element-7738">Trend Analysis</option>
            </select>
          </div>
          <div className="flex items-end" data-id="element-7739">
            <button className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7740">
              <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-7741" />
              Generate Report
            </button>
          </div>
        </div>
        {/* Report summary */}
        <div className="p-6" data-id="element-7742">
          <div className="mb-6" data-id="element-7743">
            <h4 className="text-lg font-medium text-gray-900 mb-2" data-id="element-7744">
              Expense Summary
            </h4>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3" data-id="element-7745">
              <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200" data-id="element-7746">
                <div className="p-5" data-id="element-7747">
                  <div className="flex items-center" data-id="element-7748">
                    <div className="flex-shrink-0" data-id="element-7749">
                      <DollarSignIcon className="h-6 w-6 text-indigo-600" data-id="element-7750" />
                    </div>
                    <div className="ml-5 w-0 flex-1" data-id="element-7751">
                      <dl data-id="element-7752">
                        <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-7753">
                          Total Expenses
                        </dt>
                        <dd data-id="element-7754">
                          <div className="text-lg font-medium text-gray-900" data-id="element-7755">
                            {formatCurrency(reportData.totalExpenses)}
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200" data-id="element-7756">
                <div className="p-5" data-id="element-7757">
                  <div className="flex items-center" data-id="element-7758">
                    <div className="flex-shrink-0" data-id="element-7759">
                      <TagIcon className="h-6 w-6 text-green-600" data-id="element-7760" />
                    </div>
                    <div className="ml-5 w-0 flex-1" data-id="element-7761">
                      <dl data-id="element-7762">
                        <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-7763">
                          Top {getGroupLabel()}
                        </dt>
                        <dd data-id="element-7764">
                          <div className="text-lg font-medium text-gray-900" data-id="element-7765">
                            {getDataByGrouping()[0].category || getDataByGrouping()[0].department || getDataByGrouping()[0].label}
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200" data-id="element-7766">
                <div className="p-5" data-id="element-7767">
                  <div className="flex items-center" data-id="element-7768">
                    <div className="flex-shrink-0" data-id="element-7769">
                      <UserIcon className="h-6 w-6 text-purple-600" data-id="element-7770" />
                    </div>
                    <div className="ml-5 w-0 flex-1" data-id="element-7771">
                      <dl data-id="element-7772">
                        <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-7773">
                          Top Spender
                        </dt>
                        <dd data-id="element-7774">
                          <div className="text-lg font-medium text-gray-900" data-id="element-7775">
                            {reportData.topExpenseUsers[0].name}
                          </div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Chart visualization */}
          <div className="mb-6" data-id="element-7776">
            <div className="flex justify-between items-center mb-4" data-id="element-7777">
              <h4 className="text-lg font-medium text-gray-900" data-id="element-7778">
                Expenses by {getGroupLabel()}
              </h4>
              <button className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:text-gray-800 active:bg-gray-50" data-id="element-7779">
                <DownloadIcon className="h-4 w-4 mr-1" data-id="element-7780" />
                Export
              </button>
            </div>
            {/* Simple bar chart visualization */}
            <div className="h-80 relative" data-id="element-7781">
              <div className="absolute inset-0 flex items-end" data-id="element-7782">
                {getDataByGrouping().map((item, index) => <div key={index} className="flex flex-col items-center justify-end mx-2 flex-1" data-id="element-7783">
                    <div className="w-full bg-indigo-500 rounded-t" style={{
                  height: `${item.amount / reportData.totalExpenses * 100 * 3}px`,
                  maxHeight: '90%'
                }} data-id="element-7784"></div>
                    <div className="mt-2 text-xs text-gray-500 truncate w-full text-center" data-id="element-7785">
                      {item.category || item.department || item.label}
                    </div>
                    <div className="text-xs font-medium" data-id="element-7786">
                      {formatCurrency(item.amount)}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Monthly trend */}
          <div className="mb-6" data-id="element-7787">
            <div className="flex justify-between items-center mb-4" data-id="element-7788">
              <h4 className="text-lg font-medium text-gray-900" data-id="element-7789">
                Monthly Expense Trend
              </h4>
            </div>
            {/* Simple line chart visualization */}
            <div className="h-64 relative" data-id="element-7790">
              <div className="absolute inset-0 flex items-end" data-id="element-7791">
                {reportData.monthlyTrend.map((item, index) => <div key={index} className="flex flex-col items-center justify-end mx-2 flex-1" data-id="element-7792">
                    <div className="w-full bg-green-500 rounded-t" style={{
                  height: `${item.amount / 15000 * 100 * 2}px`,
                  maxHeight: '90%'
                }} data-id="element-7793"></div>
                    <div className="mt-2 text-xs text-gray-500" data-id="element-7794">
                      {item.month}
                    </div>
                    <div className="text-xs font-medium" data-id="element-7795">
                      {formatCurrency(item.amount)}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Top expense users */}
          <div data-id="element-7796">
            <div className="flex justify-between items-center mb-4" data-id="element-7797">
              <h4 className="text-lg font-medium text-gray-900" data-id="element-7798">
                Top Expense Users
              </h4>
            </div>
            <div className="overflow-x-auto" data-id="element-7799">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-7800">
                <thead className="bg-gray-50" data-id="element-7801">
                  <tr data-id="element-7802">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7803">
                      User
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7804">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7805">
                      # of Claims
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7806">
                      % of Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-7807">
                  {reportData.topExpenseUsers.map((user, index) => <tr key={index} data-id="element-7808">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-7809">
                        {user.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-7810">
                        {formatCurrency(user.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-7811">
                        {user.count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-7812">
                        {(user.amount / reportData.totalExpenses * 100).toFixed(1)}
                        %
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>;
}